import { useEffect, useRef, useState, type ReactNode } from "react";
import { motion, useInView, type Variants } from "framer-motion";
import type { ScrollRevealConfig } from "@site-factory/config";

export interface AnimateOnScrollProps extends ScrollRevealConfig {
  children: ReactNode;
  className?: string;
  /** Tag to render (default: div) */
  as?: keyof JSX.IntrinsicElements;
}

/**
 * Animation variants for scroll reveal effects.
 */
const createVariants = (variant: ScrollRevealConfig["variant"] = "fadeUp"): Variants => {
  const distance = 40; // pixels to move

  const variants: Record<string, Variants> = {
    fade: {
      hidden: { opacity: 0 },
      visible: { opacity: 1 },
    },
    fadeUp: {
      hidden: { opacity: 0, y: distance },
      visible: { opacity: 1, y: 0 },
    },
    fadeDown: {
      hidden: { opacity: 0, y: -distance },
      visible: { opacity: 1, y: 0 },
    },
    fadeLeft: {
      hidden: { opacity: 0, x: distance },
      visible: { opacity: 1, x: 0 },
    },
    fadeRight: {
      hidden: { opacity: 0, x: -distance },
      visible: { opacity: 1, x: 0 },
    },
    slideUp: {
      hidden: { y: distance },
      visible: { y: 0 },
    },
    slideDown: {
      hidden: { y: -distance },
      visible: { y: 0 },
    },
    slideLeft: {
      hidden: { x: distance },
      visible: { x: 0 },
    },
    slideRight: {
      hidden: { x: -distance },
      visible: { x: 0 },
    },
    scale: {
      hidden: { opacity: 0, scale: 0.8 },
      visible: { opacity: 1, scale: 1 },
    },
    scaleUp: {
      hidden: { scale: 0.8 },
      visible: { scale: 1 },
    },
    scaleDown: {
      hidden: { scale: 1.1 },
      visible: { scale: 1 },
    },
  };

  return variants[variant] || variants.fadeUp;
};

/**
 * Easing curve mappings from simple names to Framer Motion values.
 */
const easingMap: Record<string, number[] | string> = {
  linear: "linear",
  easeIn: [0.4, 0, 1, 1],
  easeOut: [0, 0, 0.2, 1],
  easeInOut: [0.4, 0, 0.2, 1],
  circIn: [0.55, 0, 1, 0.45],
  circOut: [0, 0.55, 0.45, 1],
  circInOut: [0.85, 0, 0.15, 1],
  backIn: [0.36, 0, 0.66, -0.56],
  backOut: [0.34, 1.56, 0.64, 1],
  backInOut: [0.68, -0.6, 0.32, 1.6],
};

/**
 * Wrapper component that animates its children when scrolled into view.
 * Uses Intersection Observer for performance.
 *
 * @example
 * ```tsx
 * <AnimateOnScroll variant="fadeUp" duration={0.6} delay={0.2}>
 *   <h2>Animated Heading</h2>
 * </AnimateOnScroll>
 * ```
 */
export function AnimateOnScroll({
  children,
  className,
  as = "div",
  enabled = true,
  variant = "fadeUp",
  duration = 0.6,
  delay = 0,
  threshold = 0.1,
  once = true,
  staggerChildren = 0,
  easing = "easeOut",
}: AnimateOnScrollProps) {
  const ref = useRef<HTMLElement>(null);
  const isInView = useInView(ref, { once, amount: threshold });
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    if (isInView && !hasAnimated) {
      setHasAnimated(true);
    }
  }, [isInView, hasAnimated]);

  // Respect user's reduced motion preference
  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // If disabled or user prefers reduced motion, render without animation
  if (!enabled || prefersReducedMotion) {
    const Component = as;
    return <Component className={className}>{children}</Component>;
  }

  const variants = createVariants(variant);
  const easingValue = easingMap[easing] || easingMap.easeOut;

  const MotionComponent = motion[as as keyof typeof motion] as typeof motion.div;

  return (
    <MotionComponent
      ref={ref as any}
      className={className}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={variants}
      transition={{
        duration,
        delay,
        ease: easingValue as any,
        ...(staggerChildren > 0 && {
          staggerChildren,
          delayChildren: delay,
        }),
      }}
    >
      {children}
    </MotionComponent>
  );
}

/**
 * Variant for staggering children animations.
 * Wrap child elements with this to create a stagger effect.
 *
 * @example
 * ```tsx
 * <AnimateOnScroll staggerChildren={0.1}>
 *   <AnimatedChild><Card /></AnimatedChild>
 *   <AnimatedChild><Card /></AnimatedChild>
 *   <AnimatedChild><Card /></AnimatedChild>
 * </AnimateOnScroll>
 * ```
 */
export function AnimatedChild({
  children,
  className,
  as = "div",
  variant = "fadeUp",
}: {
  children: ReactNode;
  className?: string;
  as?: keyof JSX.IntrinsicElements;
  variant?: ScrollRevealConfig["variant"];
}) {
  const variants = createVariants(variant);
  const MotionComponent = motion[as as keyof typeof motion] as typeof motion.div;

  return (
    <MotionComponent className={className} variants={variants}>
      {children}
    </MotionComponent>
  );
}
